import {
  AlarmClock,
  AlertCircle,
  AlertTriangle,
  Ban,
  BellRing,
  Calendar,
  CheckCircle2,
  CircleCheck,
  Clock,
  FileText,
  Gavel,
  Hourglass,
  MessageCircle,
  PauseCircle,
  RefreshCw,
  UserCheck,
} from 'lucide-react';

import type { ArbitragemDetalhe, ArbitragemSituacao } from '../types/arbitragemDetalheTypes';
import { derivarPrazoPendencia } from './prazoPendencia';
import type { OrientacaoAcaoBannerProps } from '@/features/portal/types/orientacaoTypes';

export const isSituacaoRespondivel = (situacao: ArbitragemSituacao): boolean =>
  situacao === 'prazo_aberto' || situacao === 'prazo_acabando';

function situacaoEfetiva(arbitragem: ArbitragemDetalhe): ArbitragemSituacao {
  if (arbitragem.situacao) return arbitragem.situacao;
  if (arbitragem.podeManifestar) return 'prazo_aberto';
  return 'em_analise';
}

/**
 * Única fonte de verdade pra "o notificado ainda pode responder agora" — usada tanto pelo
 * botão fixo do cabeçalho (`ArbitragemDetalheView`) quanto pelo banner da aba de detalhes
 * (`orientacaoDoDetalhe`, via `acao`). Antes o cabeçalho tinha sua própria condição
 * (`situacao !== 'encerrado'`), que ficava `true` em qualquer situação não-terminal — inclusive
 * `manifestou`, mostrando "Quero responder" pra quem já respondeu.
 */
export function podeResponderAgora(arbitragem: ArbitragemDetalhe): boolean {
  const situacao = situacaoEfetiva(arbitragem);
  if (isSituacaoRespondivel(situacao)) return true;
  // Pedido de Esclarecimento pós-sentença (ClickUp 86ak8m7du) reusa o MESMO botão/formulário —
  // "sentenca_publicada" só habilita o CTA quando a janela do backend está aberta.
  return situacao === 'sentenca_publicada' && !!arbitragem.podePedirEsclarecimento;
}

/**
 * `derivarPrazoPendencia` já resolve data completa + "vence em N dias" para o bloco de
 * Pendências — reusar aqui em vez de reformatar a data crua evita o que o QA relatou
 * (86ak89q0z): banner mostrando só "Prazo em aberto", sem dia nem contagem, mesmo com
 * `prazoLimiteIso` preenchido pelo backend.
 */
function prazoDoBanner(arbitragem: ArbitragemDetalhe, agoraIso: string) {
  if (!arbitragem.prazoLimiteIso) return null;
  return derivarPrazoPendencia(arbitragem.prazoLimiteIso, agoraIso);
}

export function orientacaoDoDetalhe(
  arbitragem: ArbitragemDetalhe,
  agoraIso: string = new Date().toISOString(),
): OrientacaoAcaoBannerProps {
  const situacao = situacaoEfetiva(arbitragem);
  const responderHref = `/portal/arbitragem/${arbitragem.id}/responder`;
  const prazo = prazoDoBanner(arbitragem, agoraIso);
  const prazoChip = prazo ? prazo.chipRotulo : 'Prazo em aberto';

  switch (situacao) {
    case 'prazo_aberto':
      return {
        tom: 'atencao',
        icone: Clock,
        titulo: 'Chegou a sua vez de responder',
        descricao: prazo
          ? `Leia o pedido e envie sua resposta. ${prazo.detalheRotulo}.`
          : 'Leia o pedido e envie sua resposta dentro do prazo informado.',
        chips: [{ icone: Clock, texto: prazoChip }],
        acao: {
          label: 'Responder ao processo',
          icone: FileText,
          variante: 'primary',
          href: responderHref,
        },
      };
    case 'prazo_acabando':
      return {
        tom: 'urgente',
        icone: AlarmClock,
        titulo: 'Falta pouco para o seu prazo acabar',
        descricao: prazo
          ? `Leia o pedido e envie sua resposta. ${prazo.detalheRotulo}.`
          : 'O prazo termina em breve. Leia o pedido e envie sua resposta.',
        chips: [{ icone: Clock, texto: prazoChip }],
        acao: {
          label: 'Responder ao processo',
          icone: FileText,
          variante: 'primary',
          href: responderHref,
        },
      };
    case 'manifestou':
      return {
        tom: 'sucesso',
        icone: CheckCircle2,
        titulo: 'Sua resposta foi enviada',
        descricao:
          'Recebemos sua resposta. Agora é aguardar os próximos passos. Avisamos por e-mail e WhatsApp quando houver novidade.',
        chips: [{ icone: Calendar, texto: 'Nenhum prazo correndo contra você agora' }],
      };
    case 'prazo_perdido':
      return {
        tom: 'critico',
        icone: AlertTriangle,
        titulo: 'O prazo para responder terminou',
        descricao:
          'O prazo encerrou e o caso seguiu sem a sua resposta. Isso pode afetar a decisão. Se precisar de ajuda, fale com a nossa equipe.',
        chips: prazo ? [{ icone: Clock, texto: prazo.chipRotulo }] : undefined,
        acao: {
          label: 'Falar com a equipe',
          icone: MessageCircle,
          variante: 'outline',
          href: '/portal/duvidas',
        },
      };
    case 'encerrado':
      return {
        tom: 'neutro',
        icone: Gavel,
        titulo: 'Este caso foi encerrado',
        descricao:
          'A decisão final foi publicada e o caso foi concluído. Você pode ler a sentença e baixar os documentos.',
      };
    case 'aguardando_julgamento':
      return {
        tom: 'neutro',
        icone: Hourglass,
        titulo: 'Seu caso está com o especialista',
        descricao:
          'O especialista está analisando o caso para decidir. Avisamos por e-mail e WhatsApp quando a decisão sair.',
        chips: [{ icone: Calendar, texto: 'Nenhum prazo correndo contra você agora' }],
      };
    case 'sentenca_publicada': {
      // ClickUp 86ak8m7du: a janela de Pedido de Esclarecimento agora vem PRONTA do backend
      // (`citacao.esclarecimento`, mesmo prazo que já governa o trânsito em julgado) — o CTA
      // reusa o mesmo formulário de resposta, adaptado ao contexto pós-sentença. Nomenclatura
      // obrigatória: nunca "embargos de declaração" na experiência da parte.
      if (arbitragem.podePedirEsclarecimento) {
        const dias = arbitragem.prazoEsclarecimentoDiasRestantes;
        const prazoTexto =
          dias === null
            ? 'dentro do prazo'
            : dias <= 1
              ? 'até amanhã'
              : `nos próximos ${dias} dias`;
        return {
          tom: 'atencao',
          icone: Gavel,
          titulo: 'Você foi intimado da decisão do seu caso',
          descricao: `O especialista já decidiu. Se precisar, você pode pedir esclarecimentos sobre a decisão ${prazoTexto}. Depois desse prazo, a decisão se torna definitiva.`,
          chips:
            dias !== null
              ? [
                  {
                    icone: Clock,
                    texto:
                      dias === 1
                        ? '1 dia para pedir esclarecimentos'
                        : `${dias} dias para pedir esclarecimentos`,
                  },
                ]
              : undefined,
          acao: {
            label: 'Pedir esclarecimentos',
            icone: FileText,
            variante: 'primary',
            href: responderHref,
          },
        };
      }
      // Janela fechada (ou este pedido já foi enviado) — informativo, sem CTA de submissão.
      return {
        tom: arbitragem.esclarecimentoJaPedido ? 'sucesso' : 'neutro',
        icone: arbitragem.esclarecimentoJaPedido ? CheckCircle2 : Gavel,
        titulo: arbitragem.esclarecimentoJaPedido
          ? 'Seu pedido de esclarecimento foi enviado'
          : 'Você foi intimado da decisão do seu caso',
        descricao: arbitragem.esclarecimentoJaPedido
          ? 'Recebemos seu pedido de esclarecimento. Avisamos por e-mail e WhatsApp quando houver novidade.'
          : 'O especialista já decidiu e o prazo para pedir esclarecimentos já encerrou. Se precisar de ajuda, fale com a nossa equipe.',
        acao: arbitragem.esclarecimentoJaPedido
          ? undefined
          : {
              label: 'Falar com a equipe',
              icone: MessageCircle,
              variante: 'outline',
              href: '/portal/duvidas',
            },
      };
    }
    case 'erro_status':
      return {
        tom: 'atencao',
        icone: AlertCircle,
        titulo: 'Não foi possível confirmar seus prazos agora',
        descricao:
          'Não conseguimos verificar se há algo pendente para você. Atualize a página ou fale com a nossa equipe antes de considerar que não há prazo.',
      };
    case 'dessincronizado':
      return {
        tom: 'atencao',
        icone: RefreshCw,
        titulo: 'As informações podem ter mudado',
        descricao:
          'O caso teve uma atualização. Atualize para ver seus prazos e o que precisa ser feito agora.',
      };
    case 'cancelado':
      return {
        tom: 'neutro',
        icone: Ban,
        titulo: 'Este caso foi cancelado',
        descricao: 'O caso foi cancelado antes de seguir e não exige nenhuma ação sua.',
      };
    case 'suspenso':
      return {
        tom: 'atencao',
        icone: PauseCircle,
        titulo: 'Este caso está suspenso',
        descricao:
          'Os prazos estão pausados. Quando o caso voltar a correr, avisamos por e-mail e WhatsApp.',
        chips: [{ icone: Clock, texto: 'Prazo pausado' }],
      };
    case 'contato_pendente':
      return {
        tom: 'atencao',
        icone: BellRing,
        titulo: 'Confirme seus contatos',
        descricao:
          'Precisamos de um e-mail e um WhatsApp válidos para avisar você a tempo dos prazos. Confirme seus dados para não perder nada.',
      };
    case 'delegado_advogado':
      return {
        tom: 'sucesso',
        icone: UserCheck,
        titulo: 'Seu advogado está cuidando do caso',
        descricao:
          'Você indicou um advogado e ele foi habilitado. Ele recebe os prazos e responde por você — e você continua acompanhando tudo por aqui.',
      };
    case 'em_analise':
    default:
      return {
        tom: 'neutro',
        icone: CircleCheck,
        titulo: 'Por enquanto, você não precisa fazer nada',
        descricao:
          'Seu caso está seguindo os próximos passos. Avisamos por e-mail e WhatsApp quando for a sua vez.',
        chips: [{ icone: Calendar, texto: 'Nenhum prazo correndo contra você agora' }],
      };
  }
}
